import { useState } from "react";
import { Alert, Button, Drawer, Form, useToast } from "@/ui";
import { errorMessage } from "./api";
import { useChangePassword } from "./auth";
import { TextField } from "./fields";
import { EMPTY_PASSWORD_DRAFT, OPERATOR_PASSWORD_RULE, passwordErrors, type PasswordDraft, type PasswordDraftErrors } from "./password";

/** 当前用户自助改密码（docs/04 第 9 节，D201）。只读账号也能改，所以不用只给可写账号提交的 FormDrawer。
 * 成功后服务端撤销全部会话，本地随即退出到登录页，用新密码重新登录。 */
export function PasswordDrawer({ username, onClose }: { username: string; onClose: () => void }) {
  const [draft, setDraft] = useState(EMPTY_PASSWORD_DRAFT);
  const [errors, setErrors] = useState<PasswordDraftErrors>({});
  const change = useChangePassword();
  const toast = useToast();
  const set = (k: keyof PasswordDraft) => (v: string) => setDraft((d) => ({ ...d, [k]: v }));
  const submit = () => {
    const found = passwordErrors(username, draft);
    setErrors(found);
    if (Object.keys(found).length) return;
    // 成功时会话随即结束、外壳卸载，mutate 的回调不再触发，所以等 Promise；失败的原因由 change.error 显示
    change.mutateAsync({ current_password: draft.current, new_password: draft.next })
      .then(() => toast("密码已修改，请用新密码重新登录", { tone: "success" }), () => undefined);
  };
  return (
    <Drawer open title="修改密码" onClose={onClose}
      footer={<><Button onClick={onClose}>取消</Button><Button variant="primary" disabled={change.isPending} onClick={submit}>修改密码</Button></>}>
      {change.error && <Alert tone="danger" title="修改失败">{errorMessage(change.error)}</Alert>}
      <Alert title="修改后要重新登录">修改成功后，这个账号在所有浏览器上的登录都会失效，需要用新密码重新登录。</Alert>
      <Form>
        <TextField id="pw-current" label="当前密码" required type="password" value={draft.current} error={errors.current} onChange={set("current")} />
        <TextField id="pw-next" label="新密码" required type="password" value={draft.next} error={errors.next} hint={OPERATOR_PASSWORD_RULE} onChange={set("next")} />
        <TextField id="pw-confirm" label="确认新密码" required type="password" value={draft.confirm} error={errors.confirm} onChange={set("confirm")} />
      </Form>
    </Drawer>
  );
}
